import * as Yup from "yup";
import swal from "sweetalert";
import { useFormik } from "formik";
import { login, setToken, getToken, decodeToken, verifyMFA } from '../../data/users';
import { getSSOClientsPublic } from '../../data/sso-clients';
import { useHistory, Redirect, Link } from 'react-router-dom';
import { useContext, useEffect, useState } from "react";
import { UserContext } from "context/UserContext";
import { SSOClientPublic } from 'interfaces/SSOClient';
import { useTranslation } from "react-i18next";
import Loading from "components/Loading";
import bgLogin from '../../assets/login-bg.png'

const Login = () => {

  const {t, ready, i18n} = useTranslation();

  const validationSchema = Yup.object().shape({
    username: Yup.string().required(t('user_required')),
    password: Yup.string().required(t('password_required')),
  });
  const [loading, setLoading] = useState(false);
  const [ssoClients, setSSOClients] = useState<SSOClientPublic[]>([]);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  const {setUserInfo} = useContext(UserContext)

  const history = useHistory();

  useEffect(() => {
    getSSOClientsPublic()
      .then((resp: any) => setSSOClients(resp.data))
      .catch(() => {});
  }, []);

  const finishLogin = (data: any) => {
    const token = data.access;
    setToken(data);
    swal('Afinidata', t("user_valid"), 'success').then(() => {
      setLoading(false);
      const decode = decodeToken();
      setUserInfo({ token, isAdmin: decode.superuser });
      history.push('/admin/dashboard');
    });
  };

  const handleSubmit = async (payload: any): Promise<void> => {
    setLoading(true);
    login(payload)
    .then((resp: any) => {
      const { data } = resp;
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setLoading(false);
      } else {
        finishLogin(data);
      }
    })
    .catch(() => {
      setLoading(false);
      swal("Afinidata", t('user_invalid'), "error");
    });
  };

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken) return;
    setLoading(true);
    verifyMFA(mfaToken, mfaCode)
      .then((resp: any) => {
        finishLogin(resp.data);
      })
      .catch(() => {
        setLoading(false);
        swal("Afinidata", "Código incorrecto o expirado", "error");
      });
  };

  const initialValues = {
    username: "",
    password: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang)
  }

  if( getToken() ){
    return <Redirect to="/admin/dashboard" />
  }

  if(!ready){
    return <Loading />
  }

  return (
    <>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-10 col-lg-12 col-md-9">
            <div className="card o-hidden border-0 shadow-lg my-5">
              <div className="card-body p-0">
                <div className="row">
                  <div className="col-lg-6 d-none d-lg-block"
                  style={{
                    backgroundImage: `url(${bgLogin})`,
                    backgroundSize: "cover",
                    backgroundPosition: 'center center',
                    backgroundAttachment: 'scroll',
                    backgroundRepeat: 'no-repeat'
                  }}></div>
                  <div className="col-lg-6">
                    <div className="p-5">
                      <div className="text-center">
                        <img
                          src="/img/logo.png"
                          alt=""
                          style={{
                            width: "200px",
                            height: "auto",
                            display: "block",
                            margin: "12px auto",
                          }}
                        />
                      </div>
                      {mfaToken ? (
                        <form className="user" onSubmit={handleMFASubmit}>
                          <p className="text-center text-muted small mb-3">
                            Ingresa el código de 6 dígitos enviado a tu correo.
                          </p>
                          <div className="form-group">
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              placeholder="000000"
                              className="form-control form-control-user text-center"
                              style={{ letterSpacing: mfaCode ? '8px' : '0', fontSize: '20px' }}
                              value={mfaCode}
                              onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                              autoFocus
                            />
                          </div>
                          <button
                            className="btn btn-primary btn-user btn-block"
                            type="submit"
                            disabled={loading || mfaCode.length !== 6}
                          >
                            Verificar
                          </button>
                          <div className="text-center mt-2">
                            <button
                              type="button"
                              className="btn btn-link small"
                              disabled={loading}
                              onClick={async () => {
                                setMfaCode('');
                                setLoading(true);
                                try {
                                  const resp: any = await login(formik.values);
                                  if (resp.data.mfa_required) setMfaToken(resp.data.mfa_token);
                                } catch(e) {
                                  swal("Afinidata", "Error al reenviar el código", "error");
                                } finally {
                                  setLoading(false);
                                }
                              }}
                            >
                              Reenviar código
                            </button>
                            <span className="text-muted mx-2">|</span>
                            <button
                              type="button"
                              className="btn btn-link small"
                              onClick={() => { setMfaToken(null); setMfaCode(''); }}
                            >
                              Volver
                            </button>
                          </div>
                        </form>
                      ) : (
                      <form className="user" onSubmit={formik.handleSubmit}>
                        <div className="form-group">
                          <input
                            type="username"
                            placeholder={t('username')}
                            name="username"
                            className={`form-control form-control-user ${
                              formik.errors.username && formik.touched.username
                                ? "is-invalid"
                                : ""
                            }`}
                            value={formik.values.username}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                            {formik.errors.username && formik.touched.username ? (
                                <div className="invalid-feedback">{t(formik.errors.username)}</div>
                            ) : null}
                        </div>
                        <div className="form-group">
                          <input
                            type="password"
                            name="password"
                            className={`form-control form-control-user ${
                                formik.errors.password && formik.touched.password
                                  ? "is-invalid"
                                  : ""
                              }`}
                              value={formik.values.password}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder={t('password')}
                          />
                            {formik.errors.password && formik.touched.password ? (
                                <div className="invalid-feedback">{t(formik.errors.password)}</div>
                            ) : null}
                        </div>
                        <button className="btn btn-primary btn-user btn-block"
                        type="submit"
                        disabled={loading}>
                          {t("ingresar")}
                        </button>
                      </form>
                      )}
                      {!mfaToken && ssoClients.length > 0 && (
                        <>
                          <hr />
                          <div className="row">
                            <div className="col text-center">
                              <p>O ingresa con tu cuenta corporativa:</p>
                            </div>
                          </div>
                          <div className="row">
                            {ssoClients.map(client => (
                              <div className="col text-center" key={client.slug}>
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-block"
                                  onClick={() => {
                                    window.location.href = `${process.env.REACT_APP_API_URL_AUTH}/sso/login/?client=${client.slug}`;
                                  }}
                                >
                                  Ingresar con {client.name}
                                </button>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      {!mfaToken && <hr />}
                      {!mfaToken && <div className="row">
                        <div className="col text-center">
                          <p>{t('seleccionar_idioma')}:</p>
                        </div>
                      </div>}
                      {!mfaToken && <div className="row">
                        <div className="col text-center">
                            <button onClick={() => handleLanguageChange("es")}
                            className="btn btn-primary btn-sm">
                              {t('spanish')}
                            </button>
                        </div>
                        <div className="col text-center">
                            <button onClick={() => handleLanguageChange("pr")}
                            className="btn btn-primary btn-sm">
                              {t('portugues')}
                            </button>
                        </div>
                      </div>}
                      {!mfaToken && <>
                      <hr />
                      <div className="text-center">
                        <Link to="/password-forget" className="small">
                          {t('olvido_clave')}
                        </Link>
                      </div>
                      </>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
